import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  RouterFeatures,
  TitleStrategy,
  provideRouter,
  withInMemoryScrolling,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { of } from 'rxjs';

import { providePwa, provideWebPush } from '@ta/capacitor';
import { provideGTM } from '@ta/core';
import { provideForm } from '@ta/form-input';
import { LAZY_SERVICE_TOKEN, TaNotificationService } from '@ta/notification';
import { provideServer, provideStrapi } from '@ta/server';
import { TranslationSourceType, provideTranslation } from '@ta/translation';
import { TA_LANGUAGES, provideAuth0 } from '@ta/user';
import { provideUser } from '@ta/user';
import { APPLICATION_CONFIG } from '@ta/utils';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { TranslatedTitleStrategy } from './features/core/translated-title.strategy';
import { UserService } from './services/user/user.service';

export const ROUTER_FEATURES: RouterFeatures[] = [
  withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideRouter(routes, ...ROUTER_FEATURES),
    provideUser({ service: UserService }),
    provideGTM(environment.GTM),
    provideTranslation({
      default: environment.TRANSLATION.DEFAULT,
      supportedLanguages: environment.TRANSLATION.SUPPORTEDLANGUAGES,
      source: {
        type: TranslationSourceType.FILE,
        filePath: 'assets/i18n/',
      },
    }),
    // Les pipes `date`, `number` et `currency` lisent `LOCALE_ID`. Déclaré après
    // `provideTranslation`, dont la fabrique lit `currentLang` une seule fois, trop tôt sur une
    // route sans garde (la langue par défaut restait figée après une bascule).
    {
      provide: LOCALE_ID,
      useFactory: () => {
        const supported: string[] = environment.TRANSLATION.SUPPORTEDLANGUAGES;
        // `storage-manager-js` écrit la valeur en JSON.
        let stored: string | null = null;
        try {
          const raw = localStorage.getItem('lang');
          stored = raw ? ((JSON.parse(raw) as string) ?? null) : null;
        } catch {
          stored = null;
        }
        const browser = navigator.language?.split('-')[0];
        return (
          [stored, browser].find((lang) => !!lang && supported.includes(lang)) ??
          environment.TRANSLATION.DEFAULT
        );
      },
    },
    provideServer({
      graphQlConfig: environment.GRAPHQL_SERVER_CONFIG,
      restConfig: environment.REST_SERVER_CONFIG,
    }),
    provideStrapi({ strapiConfig: environment.STRAPI_SERVER_CONFIG }),
    provideAuth0({ config: environment.AUTH0 }),
    providePwa(environment.PWA),
    provideWebPush({ clientName: 'webPushService' }),
    provideForm({ googleMapsApiKey: environment.GOOGLE_MAPS_API_KEY }),
    provideNativeDateAdapter(),
    provideAppInitializer(() => {
      const iconRegistry = inject(MatIconRegistry);
      iconRegistry.setDefaultFontSetClass('material-icons-outlined');
      return of();
    }),
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    // Le sélecteur de langue s'aligne sur les langues réellement traduites.
    {
      provide: TA_LANGUAGES,
      useValue: [
        { id: 'fr', name: 'Français' },
        { id: 'nl', name: 'Nederlands' },
        { id: 'en', name: 'English' },
        { id: 'de', name: 'Deutsch' },
      ],
    },
    {
      provide: APPLICATION_CONFIG,
      useValue: { isCustomerApplication: false },
    },
    { provide: LAZY_SERVICE_TOKEN, useExisting: TaNotificationService }, //need to to move to provider
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
    // `custom-sw.js` enveloppe `ngsw-worker.js` et y ajoute le Web Push (voir src/custom-sw.js).
    provideServiceWorker('custom-sw.js', {
      enabled: environment.SERVICE_WORKER,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
